import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { Amenity } from '../../types';
interface AmenityPopupCardProps {
  amenity: Amenity;
  onClose: () => void;
  position: {
    x: number;
    y: number;
  };
}
// Map category to color styles
const getCategoryStyles = (category: Amenity['category']) => {
  switch (category) {
    case 'Recreation':
      return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    case 'Wellness':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'Social':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    default:
      return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
  }
};
export function AmenityPopupCard({
  amenity,
  onClose,
  position
}: AmenityPopupCardProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  const categoryStyle = getCategoryStyles(amenity.category);
  return <motion.div
  // Start slightly higher (40px) and animate to final position (60px)
  initial={{
    opacity: 0,
    scale: 0.9,
    y: 40
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 60
  }} exit={{
    opacity: 0,
    scale: 0.9,
    y: 40
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} className="absolute z-50 pointer-events-auto" style={{
    left: `${position.x}%`,
    top: `${position.y}%`,
    x: '-50%' // Center horizontally using Framer Motion style
  }} data-id="element-772">
      <div className="relative w-80 bg-slate-900/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl overflow-hidden" data-id="element-773">
        {/* Header Image */}
        <div className="relative h-40 overflow-hidden" data-id="element-774">
          <img src={amenity.images[0]} alt={amenity.name} className="w-full h-full object-cover" data-id="element-775" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" data-id="element-776" />

          {/* Close Button */}
          <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-slate-900/80 backdrop-blur-sm border border-white/10 text-white hover:bg-slate-800 transition-colors" aria-label="Close" data-id="element-777">
            <X className="w-4 h-4" data-id="element-778" />
          </button>

          {/* Category Badge */}
          <div className="absolute bottom-3 left-3" data-id="element-779">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border backdrop-blur-sm ${categoryStyle}`} data-id="element-780">
              {amenity.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4" data-id="element-781">
          <h3 className="text-lg font-bold text-white mb-2" data-id="element-782">{amenity.name}</h3>
          <p className="text-sm text-slate-300 leading-relaxed" data-id="element-783">
            {amenity.description}
          </p>
        </div>

        {/* Pointer arrow - pointing up to the icon */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-slate-900/95 border-l border-t border-white/10 rotate-45" data-id="element-784" />
      </div>
    </motion.div>;
}